/**
 * useOrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbDocTutorialsIntermediateUiPatternsENDocEx1Dashboard
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { emitCascadeEvent, useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbDocTutorialsIntermediateUiPatternsENBoard, OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';

// ============================================================================
// State Machine
// ============================================================================

export type OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardState =
  | 'overview'
  | 'team';

export type OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEvent =
  | 'INIT'
  | 'SHOW_TEAM'
  | 'SHOW_OVERVIEW'
  | 'MOVED';

type OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot = 'main';
interface OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardInternalState {
  machineState: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot, OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsIntermediateUiPatternsENBoard>;
}

type OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardAction =
  | { type: 'EVENT_SUCCESS'; event: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot[]; renderClear?: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbDocTutorialsIntermediateUiPatternsENBoard & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbDocTutorialsIntermediateUiPatternsENDocEx1DashboardReducer(
  state: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardInternalState,
  action: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardAction
): OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbDocTutorialsIntermediateUiPatternsENBoard>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbDocTutorialsIntermediateUiPatternsENBoard>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbDocTutorialsIntermediateUiPatternsENBoard>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardInternalState = {
  machineState: 'overview',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
}

export interface OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardLogicReturn {
  state: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot, OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsIntermediateUiPatternsENBoard>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEventPayloadMap]: (payload?: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEventPayloadMap[K]) => Promise<EventResponse> };
  config?: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardConfig;
}

export function useOrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardLogic(config?: Partial<OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardConfig>): OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardLogicReturn {
  const mergedConfig = config;

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-doc-tutorials-intermediate-ui-patterns-en-doc-ex1-dashboard', orbitalName: 'OrbDocTutorialsIntermediateUiPatternsEN', traitName: 'OrbDocTutorialsIntermediateUiPatternsENDocEx1Dashboard' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbDocTutorialsIntermediateUiPatternsENDocEx1DashboardReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['OrbDocTutorialsIntermediateUiPatternsENBoard'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'overview' && event === 'INIT') {
      // overview → overview (on INIT)
      _newState = 'overview';
    } else if (fromState === 'overview' && event === 'SHOW_TEAM') {
      // overview → team (on SHOW_TEAM)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocTutorialsIntermediateUiPatternsENDocEx1Dashboard' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsIntermediateUiPatternsEN', 'OrbDocTutorialsIntermediateUiPatternsENDocEx1Dashboard', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocTutorialsIntermediateUiPatternsEN', trait: 'OrbDocTutorialsIntermediateUiPatternsENDocEx1Dashboard' } }); })({ event: "SHOW_TEAM" as const }));
        _fields['tab'] = "team";
        fields = { ...(fields ?? {}), 'tab': _fields['tab'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'team';
    } else if (fromState === 'team' && event === 'INIT') {
      // team → overview (on INIT)
      _newState = 'overview';
    } else if (fromState === 'team' && event === 'SHOW_OVERVIEW') {
      // team → overview (on SHOW_OVERVIEW)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocTutorialsIntermediateUiPatternsENDocEx1Dashboard' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsIntermediateUiPatternsEN', 'OrbDocTutorialsIntermediateUiPatternsENDocEx1Dashboard', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocTutorialsIntermediateUiPatternsEN', trait: 'OrbDocTutorialsIntermediateUiPatternsENDocEx1Dashboard' } }); })({ event: "SHOW_OVERVIEW" as const }));
        _fields['tab'] = "overview";
        fields = { ...(fields ?? {}), 'tab': _fields['tab'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'overview';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot[]; clear: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot[] } => {
    switch (fromState) {
      case 'overview':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot[], clear: [] as OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot[] };
          case 'SHOW_TEAM': return { write: ['main'] as OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot[], clear: [] as OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'team':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot[], clear: [] as OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot[] };
          case 'SHOW_OVERVIEW': return { write: ['main'] as OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot[], clear: [] as OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbDocTutorialsIntermediateUiPatternsENBoard': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'OrbDocTutorialsIntermediateUiPatternsENDocEx1Dashboard': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEventPayloadMap]: (payload?: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'SHOW_TEAM': () => dispatch('SHOW_TEAM'),
    'SHOW_OVERVIEW': () => dispatch('SHOW_OVERVIEW'),
    'MOVED': (payload?: OrbDocTutorialsIntermediateUiPatternsENDocEx1DashboardEventPayloadMap['MOVED']) => dispatch('MOVED', payload),
  };

  useUIEvents(enqueueEvent, 'OrbDocTutorialsIntermediateUiPatternsEN.OrbDocTutorialsIntermediateUiPatternsENDocEx1Dashboard', ['INIT', 'SHOW_TEAM', 'SHOW_OVERVIEW'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbDocTutorialsIntermediateUiPatternsENDocEx1Dashboard', (): TraitStateSnapshot => ({
      traitName: 'OrbDocTutorialsIntermediateUiPatternsENDocEx1Dashboard',
      currentState: internalStateRef.current.machineState,
      states: ['overview', 'team'],
      events: ['INIT', 'SHOW_TEAM', 'SHOW_OVERVIEW', 'MOVED'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
